import i18n from "i18n"

import { COLORS } from "./constants/colors"
import { OVERVIEW_METRIC_ICON_PRESETS } from "./OverviewMetricIcon"
import { formatChartNumber } from "./utils/numberFormat"

const getEvolutionMetricColor = iconPreset =>
  COLORS[OVERVIEW_METRIC_ICON_PRESETS[iconPreset]?.accent]?.color ??
  COLORS.default.color

const COST_BY_TYPE_ORDER = [
  "unclassified",
  "cost",
  "waste_classification",
  "additional_waste_components",
  "machinery_and_auxiliary_means",
  "labor",
  "material",
]

const toArray = value => (Array.isArray(value) ? value : [])

const toNumber = value => {
  const number = Number(value ?? 0)
  return Number.isFinite(number) ? number : 0
}

const sumBy = (items, getValue) =>
  items.reduce((total, item) => total + toNumber(getValue(item)), 0)

const getClosedCertificationsByRevisionDate = certifications => {
  const certifiedByDate = aggregateAmountsByDate(
    certifications,
    "revision_date",
    "certified_total",
  )
  const invoicedByDate = aggregateAmountsByDate(
    certifications,
    "revision_date",
    "invoiced_total",
  )
  const revisionDates = [
    ...new Set([
      ...Object.keys(certifiedByDate),
      ...Object.keys(invoicedByDate),
    ]),
  ].sort()

  return revisionDates.map(revisionDate => {
    const certified = toNumber(certifiedByDate[revisionDate])
    const invoiced = toNumber(invoicedByDate[revisionDate])
    const pending = certified - invoiced
    const invoicedPct =
      certified > 0 ? Math.min(100, getPercent(invoiced, certified)) : 0

    return {
      id: revisionDate,
      revisionDate,
      certified,
      invoiced,
      pending,
      invoicedPct,
    }
  })
}

const aggregateAmountsByDate = (items, dateKey, valueKey) =>
  items.reduce((acc, item) => {
    const date = item?.[dateKey]

    if (!date) return acc

    acc[date] = (acc[date] ?? 0) + toNumber(item?.[valueKey])
    return acc
  }, {})

const mergeAmountsByDate = (a, b) => {
  const keys = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])
  return [...keys].reduce((acc, date) => {
    acc[date] = toNumber(a?.[date]) + toNumber(b?.[date])
    return acc
  }, {})
}

const getExpenseTimelineDate = item =>
  item?.date ?? item?.expense_date ?? item?.imputation_date

const getExpenseImputedValue = item =>
  toNumber(item?.imputed_amount ?? item?.imputedAmount)

const aggregateExpenseImputedByDate = items =>
  items.reduce((acc, item) => {
    const date = getExpenseTimelineDate(item)
    if (!date) return acc
    acc[date] = (acc[date] ?? 0) + getExpenseImputedValue(item)
    return acc
  }, {})

const buildCumulativeSeries = (name, dates, amountsByDate, color) => {
  let accumulated = 0

  return {
    name,
    color,
    data: dates.map(date => {
      accumulated += toNumber(amountsByDate?.[date])
      return { x: date, y: accumulated }
    }),
  }
}

const getPercent = (value, total) => {
  if (!total) return 0
  return (value / total) * 100
}

const getTrendArrow = value => {
  if (value < 0) return "down"
  if (value > 0) return "up"
  return "forward"
}

const getTrendColor = value => {
  if (value < 0) return "error"
  if (value > 0) return "success"
  return "default"
}

const formatSignedPercent = value => {
  const number = Number(value)
  if (!Number.isFinite(number)) return "0,00%"

  const formatted = Math.abs(number).toFixed(2).replace(".", ",")
  if (number > 0) return `+${formatted}%`
  if (number < 0) return `-${formatted}%`
  return `${formatted}%`
}

const formatOverviewPercent = value => {
  const number = Number(value)
  if (!Number.isFinite(number)) return "0,00%"
  return `${number.toFixed(2).replace(".", ",")}%`
}

const getCostByTypeLabel = type => {
  switch (type) {
    case "material":
      return i18n.t("overview.materials")
    case "labor":
      return i18n.t("overview.labor")
    case "machinery_and_auxiliary_means":
      return i18n.t("overview.machineryAndAuxiliaryMeans")
    case "additional_waste_components":
      return i18n.t("overview.additionalWasteComponents")
    case "waste_classification":
      return i18n.t("overview.wasteClassification")
    case "cost":
      return i18n.t("overview.cost")
    case "unclassified":
      return i18n.t("overview.unclassified")
    default:
      return type
  }
}

export const buildPlanSectionData = (projectSummary, currency = "") => {
  const costObjective = toNumber(projectSummary?.parts?.cost_objetive)
  const clientPrice = toNumber(projectSummary?.parts?.client_price)
  const marginType = projectSummary?.parts?.margin_type
  const costByType = projectSummary?.parts?.cost_by_type ?? {}
  const costByTypeChart =
    Object.keys(costByType).length > 0
      ? COST_BY_TYPE_ORDER.map(type => ({
          x: getCostByTypeLabel(type),
          y: toNumber(costByType?.[type]),
        }))
      : []

  const marginDifference = clientPrice - costObjective
  const objectiveMarginBase =
    marginType === "CLIENT" ? clientPrice : costObjective
  const objectiveMarginPercentage = getPercent(
    marginDifference,
    objectiveMarginBase,
  )
  const objectiveMarginNeutral = objectiveMarginBase === 0

  return {
    currency,
    cards: [
      {
        id: "objectiveCost",
        iconPreset: "objectiveCost",
        title: i18n.t("overview.objectiveCost"),
        value: costObjective,
        symbol: currency,
        infoText: i18n.t("overview.tooltip.objectiveCost"),
        helper: i18n.t("overview.planObjectiveCostHelper"),
      },
      {
        id: "objectiveMargin",
        iconPreset: "objectiveMargin",
        title: i18n.t("overview.objectiveMargin"),
        value: marginDifference,
        symbol: currency,
        infoText: i18n.t("overview.tooltip.objectiveMargin"),
        chipValue: formatSignedPercent(objectiveMarginPercentage),
        chipArrow: objectiveMarginNeutral
          ? "forward"
          : getTrendArrow(objectiveMarginPercentage),
        accentColor: objectiveMarginNeutral
          ? "default"
          : getTrendColor(objectiveMarginPercentage),
      },
      {
        id: "saleAmount",
        iconPreset: "saleAmount",
        title: i18n.t("overview.saleAmount"),
        value: clientPrice,
        symbol: currency,
        infoText: i18n.t("overview.tooltip.saleAmount"),
        helper: i18n.t("overview.planSaleAmountHelper"),
      },
    ],
    costByTypeChart,
  }
}

export const buildExpensesSectionData = (projectSummary, currency = "") => {
  const expenses = toArray(projectSummary?.expenses)

  const expenseTotal = sumBy(expenses, expense => expense?.subtotal_amount)
  const expenseTotalWithTaxes = sumBy(
    expenses,
    expense => expense?.total_amount,
  )
  const expenseImputationTotal = sumBy(
    expenses,
    expense => expense?.imputed_amount,
  )
  const expenseToImpute = expenseTotal - expenseImputationTotal
  const expensesPendingImputationCount = expenses.filter(
    expense =>
      toNumber(expense?.subtotal_amount) > toNumber(expense?.imputed_amount),
  ).length
  const direct = sumBy(
    expenses.filter(expense => expense?.is_direct === true),
    expense => expense?.subtotal_amount,
  )
  const indirect = expenseTotal - direct

  const breakdown = expenses.reduce(
    (acc, expense) => {
      const amount = toNumber(expense?.subtotal_amount)

      switch (expense?.type) {
        case "expense_labor":
          acc.labor += amount
          break
        case "expense_material":
          acc.materials += amount
          break
        case "expense_machinery":
          acc.machinery += amount
          break
        default:
          acc.others += amount
          break
      }

      return acc
    },
    { labor: 0, materials: 0, machinery: 0, others: 0 },
  )

  return {
    currency,
    summary: {
      expenseTotal,
      expenseTotalWithTaxes,
      expenseImputationTotal,
      expenseToImpute,
      expensesPendingImputationCount,
    },
    natureChart: [
      {
        x: i18n.t("overview.direct"),
        y: direct,
        text: i18n.t("overview.direct"),
        fill: "#B5C0FD",
      },
      {
        x: i18n.t("overview.indirect"),
        y: indirect,
        text: i18n.t("overview.indirect"),
        fill: "#FDEFC6",
      },
    ],
    breakdownChart: [
      {
        x: i18n.t("overview.labor"),
        y: breakdown.labor,
        text: i18n.t("overview.labor"),
        fill: "#B5C0FD",
      },
      {
        x: i18n.t("overview.materials"),
        y: breakdown.materials,
        text: i18n.t("overview.materials"),
        fill: "#FDEFC6",
      },
      {
        x: i18n.t("overview.machinery"),
        y: breakdown.machinery,
        text: i18n.t("overview.machinery"),
        fill: "#8B5CF6",
      },
      {
        x: i18n.t("overview.others"),
        y: breakdown.others,
        text: i18n.t("overview.others"),
        fill: "#10B981",
      },
    ],
  }
}

export const buildCertificationsSectionData = (
  projectSummary,
  currency = "",
) => {
  const invoices = toArray(projectSummary?.invoices)
  const expenses = toArray(projectSummary?.expenses)
  const certifications = toArray(projectSummary?.certifications)

  const certifiedAmountsByDate = aggregateAmountsByDate(
    certifications,
    "revision_date",
    "certified_total",
  )
  const certificationInvoicedAmountsByDate = aggregateAmountsByDate(
    certifications,
    "revision_date",
    "invoiced_total",
  )
  const invoiceAmountsByDate = aggregateAmountsByDate(
    invoices,
    "issue_date",
    "amount",
  )
  const imputedAmountsByDate = aggregateExpenseImputedByDate(expenses)

  const invoicedAmountsByDate = mergeAmountsByDate(
    certificationInvoicedAmountsByDate,
    invoiceAmountsByDate,
  )

  const allDates = Array.from(
    new Set([
      ...Object.keys(invoicedAmountsByDate),
      ...Object.keys(certifiedAmountsByDate),
      ...Object.keys(imputedAmountsByDate),
    ]),
  ).sort()

  const costObjective = toNumber(projectSummary?.parts?.cost_objetive)
  const clientPrice = toNumber(projectSummary?.parts?.client_price)
  const certBase = projectSummary?.cert_base
  const saleAmount = certBase === "cost" ? costObjective : clientPrice

  const accumulatedExpenses = sumBy(expenses, item =>
    getExpenseImputedValue(item),
  )
  const accumulatedCertified = sumBy(
    certifications,
    cert => cert?.certified_total,
  )
  const accumulatedInvoiced = sumBy(invoices, invoice => invoice?.amount)

  const sortedCerts = [...certifications].sort((a, b) =>
    String(a?.revision_date ?? "").localeCompare(
      String(b?.revision_date ?? ""),
    ),
  )
  const latestCert = sortedCerts[sortedCerts.length - 1]
  const totalCertified = toNumber(latestCert?.certified_total)
  const totalInvoiced = toNumber(latestCert?.invoiced_total)
  const certifiedInvoicedGap = totalCertified - totalInvoiced

  const totalPaidAmount = sumBy(
    invoices.filter(
      invoice => `${invoice?.status ?? ""}`.toLowerCase() === "paid",
    ),
    invoice => invoice?.amount,
  )
  const unpaidInvoiceCount = invoices.filter(
    invoice => `${invoice?.status ?? ""}`.toLowerCase() !== "paid",
  ).length
  const invoicedCollectedGap = accumulatedInvoiced - totalPaidAmount

  const cards = [
    {
      id: "accumulatedExpenses",
      iconPreset: "evolutionAccumulatedExpenses",
      title: i18n.t("overview.evolutionAccumulatedExpensesTitle"),
      value: accumulatedExpenses,
      symbol: currency,
      infoText: i18n.t("overview.evolutionAccumulatedExpensesTooltip"),
      helper: i18n.t("overview.evolutionAccumulatedExpensesHelper", {
        percent: formatOverviewPercent(
          getPercent(accumulatedExpenses, costObjective),
        ),
      }),
    },
    {
      id: "accumulatedCertifications",
      iconPreset: "evolutionAccumulatedCertifications",
      title: i18n.t("overview.evolutionAccumulatedCertificationsTitle"),
      value: accumulatedCertified,
      symbol: currency,
      infoText: i18n.t("overview.evolutionAccumulatedCertificationsTooltip"),
      helper: i18n.t("overview.evolutionAccumulatedCertificationsHelper", {
        percent: formatOverviewPercent(
          getPercent(accumulatedCertified, saleAmount),
        ),
      }),
    },
    {
      id: "accumulatedInvoicing",
      iconPreset: "evolutionAccumulatedInvoicing",
      title: i18n.t("overview.evolutionAccumulatedInvoicingTitle"),
      value: accumulatedInvoiced,
      symbol: currency,
      infoText: i18n.t("overview.evolutionAccumulatedInvoicingTooltip"),
      helper: i18n.t("overview.evolutionAccumulatedInvoicingHelper", {
        salePercent: formatOverviewPercent(
          getPercent(accumulatedInvoiced, saleAmount),
        ),
        certifiedPercent: formatOverviewPercent(
          getPercent(accumulatedInvoiced, totalCertified),
        ),
      }),
    },
  ]

  return {
    currency,
    cards,
    gaps: {
      certifiedInvoicedGap,
      totalCertified,
      totalInvoiced,
      invoicedCollectedGap,
      totalInvoiceAmount: accumulatedInvoiced,
      totalPaidAmount,
      unpaidInvoiceCount,
    },
    lineChart: {
      dates: allDates,
      series: [
        buildCumulativeSeries(
          i18n.t("overview.evolutionLineSeriesInvoicing"),
          allDates,
          invoicedAmountsByDate,
          getEvolutionMetricColor("evolutionAccumulatedInvoicing"),
        ),
        buildCumulativeSeries(
          i18n.t("overview.evolutionLineSeriesCertifications"),
          allDates,
          certifiedAmountsByDate,
          getEvolutionMetricColor("evolutionAccumulatedCertifications"),
        ),
        buildCumulativeSeries(
          i18n.t("overview.evolutionLineSeriesExpenses"),
          allDates,
          imputedAmountsByDate,
          getEvolutionMetricColor("evolutionAccumulatedExpenses"),
        ),
      ].filter(serie => serie.data.some(point => point.y !== 0)),
    },
  }
}

export const buildInvoicingSectionData = (projectSummary, currency = "") => {
  const invoices = toArray(projectSummary?.invoices)
  const certifications = toArray(projectSummary?.certifications)
  const costObjective = toNumber(projectSummary?.parts?.cost_objetive)
  const clientPrice = toNumber(projectSummary?.parts?.client_price)
  const certBase = projectSummary?.cert_base
  const saleAmount = certBase === "cost" ? costObjective : clientPrice

  const totalInvoiceAmount = sumBy(invoices, invoice => invoice?.amount)
  const invoiceCount = invoices.length

  const sortedCerts = [...certifications].sort((a, b) =>
    String(a?.revision_date ?? "").localeCompare(
      String(b?.revision_date ?? ""),
    ),
  )
  const latestCert = sortedCerts[sortedCerts.length - 1]
  const totalCertified = toNumber(latestCert?.certified_total)
  const totalInvoicedOnCerts = toNumber(latestCert?.invoiced_total)
  const certifiedInvoicedGap = Math.max(
    0,
    totalCertified - totalInvoicedOnCerts,
  )
  const invoicedOverSalePct = getPercent(totalInvoiceAmount, saleAmount)
  const invoicedOverCertifiedPct =
    certifiedInvoicedGap <= 0.01
      ? 100
      : totalCertified > 0
        ? Math.min(100, getPercent(totalInvoicedOnCerts, totalCertified))
        : 0
  const closedCertificationsRows =
    getClosedCertificationsByRevisionDate(certifications)

  return {
    currency,
    cards: [
      {
        id: "invoicingTotal",
        iconPreset: "invoicingTotal",
        title: i18n.t("overview.invoicingTotalCardTitle"),
        value: totalInvoiceAmount,
        symbol: currency,
        infoText: i18n.t("overview.invoicingTotalCardTooltip"),
        helper: i18n.t(
          invoiceCount === 1
            ? "overview.invoicingTotalCardHelper_one"
            : "overview.invoicingTotalCardHelper_other",
          { count: invoiceCount },
        ),
      },
      {
        id: "invoicingSalePct",
        iconPreset: "invoicingPct",
        title: i18n.t("overview.invoicingSalePctCardTitle"),
        value: invoicedOverSalePct,
        symbol: "%",
        infoText: i18n.t("overview.invoicingSalePctCardTooltip"),
        helper: i18n.t("overview.invoicingSalePctCardHelper"),
      },
      {
        id: "invoicingPendingOnCerts",
        iconPreset: "invoicingPending",
        title: i18n.t("overview.invoicingPendingOnCertsCardTitle"),
        value: certifiedInvoicedGap,
        symbol: currency,
        infoText: i18n.t("overview.invoicingPendingOnCertsCardTooltip"),
        helper: i18n.t("overview.invoicingPendingOnCertsCardHelper"),
      },
      {
        id: "invoicingCertifiedPct",
        iconPreset: "invoicingCertifiedPct",
        title: i18n.t("overview.invoicingCertifiedPctCardTitle"),
        value: invoicedOverCertifiedPct,
        symbol: "%",
        infoText: i18n.t("overview.invoicingCertifiedPctCardTooltip"),
        helper: i18n.t("overview.invoicingCertifiedPctCardHelper"),
      },
    ],
    statusBarChart: [
      {
        x: i18n.t("overview.invoicingAmountByStatusChartInvoiced"),
        y: totalInvoiceAmount,
        color: getEvolutionMetricColor("invoicingTotal"),
      },
      {
        x: i18n.t("overview.invoicingAmountByStatusChartPending"),
        y: certifiedInvoicedGap,
        color: getEvolutionMetricColor("invoicingPending"),
      },
    ],
    closedCertificationsRows,
  }
}

export const buildCollectionsSectionData = (
  projectSummary,
  currency = "",
  projectId = "",
) => {
  const invoices = toArray(projectSummary?.invoices)
  const totalInvoiceAmount = sumBy(invoices, invoice => invoice?.amount)
  const totalPaidAmount = sumBy(
    invoices.filter(
      invoice => `${invoice?.status ?? ""}`.toLowerCase() === "paid",
    ),
    invoice => invoice?.amount,
  )
  const invoicedCollectedGap = totalInvoiceAmount - totalPaidAmount
  const pendingCollectionAmount = Math.max(0, invoicedCollectedGap)
  const collectedOverInvoicedPct =
    totalInvoiceAmount > 0
      ? Math.min(100, getPercent(totalPaidAmount, totalInvoiceAmount))
      : 0

  const today = new Date()
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`

  const overdueInvoices = invoices.filter(inv => {
    if (`${inv?.status ?? ""}`.toLowerCase() === "paid") return false
    const dueYmd =
      inv?.due_date == null || inv.due_date === ""
        ? null
        : String(inv.due_date).trim()
    if (!dueYmd || !/^\d{4}-\d{2}-\d{2}$/.test(dueYmd)) return false
    return dueYmd < todayYmd
  })
  const overdueAmount = sumBy(overdueInvoices, inv => inv?.amount)
  const overdueCount = overdueInvoices.length
  const hasOverdue = overdueCount > 0
  const overdueChipValue = hasOverdue
    ? i18n.t(
        overdueCount === 1
          ? "overview.collectionsOverdueCardBadgeOverdueInvoices_one"
          : "overview.collectionsOverdueCardBadgeOverdueInvoices_other",
        { count: overdueCount },
      )
    : i18n.t("overview.collectionsOverdueCardBadgeNoOverdue")

  return {
    currency,
    cards: [
      {
        id: "collectionsTotalCollected",
        iconPreset: "collectionsTotalCollected",
        title: i18n.t("overview.collectionsTotalCollectedCardTitle"),
        value: totalPaidAmount,
        symbol: currency,
        infoText: i18n.t("overview.collectionsTotalCollectedCardTooltip"),
        helper: i18n.t("overview.collectionsTotalCollectedCardHelper"),
        ...(projectId
          ? {
              headerActionHref: `/invoices/${projectId}?payment=paid`,
              headerActionTooltip: i18n.t(
                "overview.collectionsTotalCollectedInvoicesLinkTooltip",
              ),
            }
          : {}),
      },
      {
        id: "collectionsPendingCollection",
        iconPreset: "collectionsPendingCollection",
        title: i18n.t("overview.collectionsPendingCollectionCardTitle"),
        value: pendingCollectionAmount,
        symbol: currency,
        infoText: i18n.t("overview.collectionsPendingCollectionCardTooltip"),
        helper: i18n.t("overview.collectionsPendingCollectionCardHelper", {
          invoiced: formatChartNumber(totalInvoiceAmount, 2),
          currency,
        }),
        ...(projectId
          ? {
              headerActionHref: `/invoices/${projectId}?payment=unpaid`,
              headerActionTooltip: i18n.t(
                "overview.collectionsPendingCollectionInvoicesLinkTooltip",
              ),
            }
          : {}),
      },
      {
        id: "collectionsCollectedPct",
        iconPreset: "collectionsCollectedPct",
        title: i18n.t("overview.collectionsCollectedPctCardTitle"),
        value: collectedOverInvoicedPct,
        symbol: "%",
        infoText: i18n.t("overview.collectionsCollectedPctCardTooltip"),
        helper: i18n.t("overview.collectionsCollectedPctCardHelper"),
      },
      {
        id: "collectionsOverdue",
        iconPreset: "collectionsOverdue",
        title: i18n.t("overview.collectionsOverdueCardTitle"),
        value: overdueAmount,
        symbol: currency,
        infoText: i18n.t("overview.collectionsOverdueCardTooltip"),
        chipValue: overdueChipValue,
        accentColor: hasOverdue ? "warning" : "success",
      },
    ],
  }
}

export const buildControlVsAvanceSectionData = (
  projectSummary,
  currency = "",
) => {
  const certifications = toArray(projectSummary?.certifications)
  const expenses = toArray(projectSummary?.expenses)
  const certBase = projectSummary?.cert_base
  const clientPrice = toNumber(projectSummary?.parts?.client_price)
  const costObjective = toNumber(projectSummary?.parts?.cost_objetive)

  // Calcular avance real de obra (%)
  const certifiedAccumulated = sumBy(
    certifications,
    cert => cert?.certified_total,
  )

  let saleAmount = 0
  if (certBase === "cost") {
    saleAmount = costObjective
  } else {
    saleAmount = clientPrice
  }

  const advancePercentage =
    saleAmount > 0 ? (certifiedAccumulated / saleAmount) * 100 : 0

  const lastCertification = certifications[certifications.length - 1]
  const periodCertified = toNumber(lastCertification?.certified_total)

  const costObjectiveCurrent = costObjective
  const expectedCostAtDate = costObjectiveCurrent * (advancePercentage / 100)

  const realCostImputed = sumBy(expenses, expense => expense?.imputed_amount)

  const deviationAtDate = realCostImputed - expectedCostAtDate
  const deviationAtDatePercentage =
    expectedCostAtDate !== 0
      ? (deviationAtDate / expectedCostAtDate) * 100
      : deviationAtDate === 0
        ? 0
        : null

  const eacCost = toNumber(projectSummary?.forecast_costs?.eac_cost)
  const objectiveMarginAmount = clientPrice - costObjective
  const finalExpectedMarginSigned = clientPrice - eacCost
  const finalExpectedMarginAbsolute = Math.abs(finalExpectedMarginSigned)
  const finalExpectedMarginRentabilityPercent =
    clientPrice !== 0 ? (finalExpectedMarginSigned / clientPrice) * 100 : null

  const vsObjectiveMarginGap = finalExpectedMarginSigned - objectiveMarginAmount

  return {
    currency,
    workProgress: {
      percentage: Math.round(advancePercentage * 100) / 100,
      certifiedAccumulated,
      periodCertified,
      saleAmount,
    },
    expectedCostCard: {
      expectedCostAtDate,
      costObjective: costObjectiveCurrent,
      advancePercentage: Math.round(advancePercentage * 100) / 100,
      hasCertifications: certifications.length > 0,
      realCostImputed,
      deviationAtDate,
      deviationAtDatePercentage,
    },
    finalExpectedMarginCard: {
      marginAbsolute: finalExpectedMarginAbsolute,
      rentabilityPercent: finalExpectedMarginRentabilityPercent,
      vsObjectiveMarginGap,
    },
  }
}

export const buildForecastSectionData = (projectSummary, currency = "") => {
  const eacCost = toNumber(projectSummary?.forecast_costs?.eac_cost)
  const costObjective = toNumber(projectSummary?.parts?.cost_objetive)
  const eacVsObjectiveDeviation = eacCost - costObjective
  const eacVsObjectiveDeviationPercent =
    costObjective !== 0 ? (eacVsObjectiveDeviation / costObjective) * 100 : null

  return {
    currency,
    eac_cost: eacCost,
    eacVsObjectiveCard: {
      costObjective,
      deviation: eacVsObjectiveDeviation,
      deviationPercent: eacVsObjectiveDeviationPercent,
    },
  }
}
